import React, { useState, useEffect, useCallback } from 'react';
import { Lightbulb, TrendingUp, Clock, Award, Layers } from 'lucide-react';
import api from '../api';
import { PageHeader, EmptyState, ListSkeleton } from '../components/ui';
import ClientePicker from '../components/ClientePicker';

const FMT_DOT = {
  'Reel': '#a855f7', 'Carosello': '#00d4ff', 'Storia': '#f59e0b', 'Post Singolo': '#10b981',
  'Articolo SEO': '#3b82f6', 'Video YT': '#f43f5e', 'Newsletter': '#14b8a6',
};
const nf = (n) => Number(n || 0).toLocaleString('it-IT');

// Barra orizzontale di confronto
function BarRow({ label, value, max, color, extra }) {
  const pct = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{nf(value)} <span style={{ color: 'var(--text-dim)' }}>media {extra}</span></span>
      </div>
      <div style={{ height: 9, borderRadius: 999, background: 'var(--surface)', overflow: 'hidden', border: '1px solid var(--border)' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color || 'var(--cyan)', borderRadius: 999, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
}

function Panel({ icon: Icon, title, hint, children }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Icon size={16} color="var(--cyan)" />
        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>{title}</span>
      </div>
      {hint && <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginBottom: 12 }}>{hint}</div>}
      {children}
    </div>
  );
}

export default function CosaFunziona() {
  const [clienti, setClienti] = useState([]);
  const [cliente, setCliente] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/clienti').then(r => { setClienti(r.data || []); if (r.data && r.data[0]) setCliente(c => c || r.data[0].id); }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!cliente) { setData(null); return; }
    setLoading(true);
    api.get('/meta_analytics/what-works', { params: { cliente_id: cliente } })
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [cliente]);
  useEffect(() => { load(); }, [load]);

  const maxFmt = data ? Math.max(1, ...data.per_formato.map(x => x.media)) : 1;
  const maxFas = data ? Math.max(1, ...data.per_fascia.map(x => x.media)) : 1;
  const bestFmt = data?.per_formato?.[0];
  const bestFas = data?.per_fascia?.filter(x => x.fascia !== 'n/d')?.[0];

  return (
    <div className="eden-page">
      <PageHeader title="Cosa funziona" icon={Lightbulb}
        subtitle="Dai contenuti pubblicati: quali formati e orari rendono di più" />

      <ClientePicker clienti={clienti} selected={cliente} onSelect={c => setCliente(c.id)} />

      {loading ? (
        <ListSkeleton rows={4} />
      ) : !data || data.totale === 0 ? (
        <EmptyState icon={TrendingUp} title="Ancora nessun dato"
          hint="Quando i contenuti vengono pubblicati e arrivano gli insights da PostProxy, qui vedrai cosa funziona meglio. Usa 'Sincronizza' in Marketing Analytics per aggiornarli." />
      ) : (
        <>
          {/* Riepilogo / suggerimento */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--cyan-dim)', border: '1px solid var(--cyan)', borderRadius: 12, padding: '12px 16px', marginBottom: 18, flexWrap: 'wrap' }}>
            <Award size={18} color="var(--cyan)" style={{ flexShrink: 0 }} />
            <div style={{ fontSize: 13, color: 'var(--text)' }}>
              Su <b>{data.totale}</b> contenuti pubblicati, rende di più il formato <b style={{ color: 'var(--cyan)' }}>{bestFmt?.formato}</b>
              {bestFas ? <> e la fascia <b style={{ color: 'var(--cyan)' }}>{bestFas.fascia}</b></> : null}. Punta lì.
            </div>
          </div>

          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', marginBottom: 16 }}>
            <Panel icon={Layers} title="Per formato" hint="Media dell'engagement per tipo di contenuto">
              {data.per_formato.map(x => (
                <BarRow key={x.formato} label={x.formato} value={x.media} max={maxFmt}
                  color={FMT_DOT[x.formato] || 'var(--cyan)'} extra={`· ${x.n} post`} />
              ))}
            </Panel>
            <Panel icon={Clock} title="Per fascia oraria" hint="Quando pubblicare per rendere di più">
              {data.per_fascia.map(x => (
                <BarRow key={x.fascia} label={x.fascia} value={x.media} max={maxFas}
                  color="var(--purple)" extra={`· ${x.n} post`} />
              ))}
            </Panel>
          </div>

          <Panel icon={TrendingUp} title="Top contenuti" hint="I post pubblicati con più engagement">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              {data.top.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 11px', borderRadius: 10, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-dim)', width: 20, textAlign: 'center' }}>{i + 1}</span>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: FMT_DOT[p.formato] || 'var(--text-dim)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.titolo || 'Contenuto'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{p.formato}{p.data ? ` · ${p.data}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
                    {Object.entries(p.metrics).filter(([k]) => k !== 'on').slice(0, 3).map(([k, v]) => (
                      <div key={k} style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cyan)' }}>{nf(v)}</div>
                        <div style={{ fontSize: 9.5, color: 'var(--text-dim)', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
